"use client"

import styled from "@emotion/styled"
import { IconButton } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import { cultureMap, Link } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { Culture } from "utils/intl"
import { FetchMenusQuery } from "../../../apollo"
import { Desktop, Mobile } from "../../common/bit/MediaQueries"
import Close from "../../icons/Close"
import Menu from "../../icons/Menu"
import Search from "../../icons/Search"
import { sharedColors } from "../../theme/colors"
import {
  delayTime,
  transitionEase,
  transitionTime,
  useInViewConfig,
} from "../../transitions/utils"
import { HeaderData } from "../Layout"
import ContactMenu from "./ContactMenu"
import SideDrawer from "./Drawer"
import LanguageMenu from "./LanguageMenu"
import NavLink from "./MenuItem"
import { SearchModal } from "./SearchModal"

const StyledHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 64px;
  background-color: ${sharedColors.brandDark};

  z-index: 101;
  display: flex;
  align-items: center;
  ${(p) => p.theme.breakpoints.up("md")} {
    box-shadow: 0px 4px 20px 0px #26303d26;
    background-color: white;
    height: 80px;
  }
`

const InnerHeader = styled.div<{ $inView: boolean }>`
  background-color: white;
  padding: 0 16px;

  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  top: 0;
  opacity: 0;
  transform: translateY(-100%);
  animation-name: slideDown;
  animation-duration: ${transitionTime}s;
  animation-timing-function: ${transitionEase};
  animation-fill-mode: forwards;
  animation-delay: ${delayTime}s;
  box-shadow: 0px 4px 20px 0px #26303d26;
  @keyframes slideDown {
    0% {
      opacity: 0;
      transform: translateY(-100%);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    box-shadow: none;
    max-width: 1280px;
    padding: 0 88px;
    margin: auto;
    /* Gap between logo / menu / actions. Matches the menu's own item gap so
       the spacing reads as one even rhythm; the menu is pinned to the logo
       side (see the menu wrapper) and any leftover space falls between the
       menu and the actions, matching the Figma. */
    column-gap: 16px;
    /* Let the menu column shrink below its content width so a wide menu
       tightens instead of pushing the actions (language switcher) off-screen. */
    & > div {
      min-width: 0;
    }
  }
  ${(p) => p.theme.breakpoints.up("xl")} {
    padding: 0 240px;
    max-width: 1920px;
    column-gap: 48px;
  }
`

const MenuNav = styled.nav`
  display: flex;
  align-items: center;
  /* Fill the available column and spread items across it, so the menu uses
     free space when there is any. gap is the minimum spacing (kept equal to
     the InnerHeader column-gap) so items never collide when space is tight;
     min-width:0 lets the menu shrink instead of pushing the actions off. */
  width: 100%;
  min-width: 0;
  justify-content: space-between;
  gap: 16px;
  height: 100%;
  ${(p) => p.theme.breakpoints.up("xl")} {
    gap: 48px;
  }
`

const ActionsDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 100%;
`

const Divider = styled.div`
  display: none;

  ${(p) => p.theme.breakpoints.up("md")} {
    display: block;
    height: 40px;
    width: 1px;
    background-color: #d1d5db;
  }
`

export const StyledIconButton = styled(IconButton)<{ active?: boolean }>`
  padding: 0;
  margin: 0;
  height: 32px;
  width: 32px;
  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    height: 40px;
    width: 40px;
  }

  border-radius: 8px;
  ${({ active }) => active && `background-color: #F5F6F8;`}
  &:hover {
    background-color: white;
    border: 1px solid ${sharedColors.brandDark};
  }
`

export const StyledWrapperBurger = styled.div`
  ${(p) => p.theme.breakpoints.down("md")} {
    width: 64px;
  }
`

// The logo keeps its intrinsic width and never shrinks; spacing to the menu
// is guaranteed by the InnerHeader column-gap, so it works for any menu
// content in both directions (RTL/LTR).
const StyledLogoLink = styled(Link)`
  flex-shrink: 0;
`

export type MenuItemChild = {
  label: string
  href: string
}
export type MenuItemData = {
  menuName: string
  label: string
  href: string
  items: MenuItemChild[]
}

export default function Header({
  locale,
  menu_data,
  header,
}: {
  locale: string
  menu_data: FetchMenusQuery
  header: HeaderData
}) {
  const t = useTranslations("Homepage")
  const culture: Culture = cultureMap[locale] || "he-il"
  const [modalOpen, setModalOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const menus = menu_data.menu_desktop?.children || []
  const menusmap: MenuItemData[] = menus.map((el: any) => {
    const label = el.properties?.[0]?.property.menuName
    const href = el.properties?.[1]?.property.menuTarget?.[0]?.url || ""
    const menuName = el.properties?.[1]?.property?.menuName || ""
    const children = el.children || []
    const childrenmap = children.map((i: any) => {
      const label = i.properties?.[0]?.property.menuName
      const secondProperty = i.properties?.[1]?.property
      const href =
        secondProperty.menuTarget?.[0]?.url || secondProperty.menuName || ""
      return { label, href }
    }) as { label: string; href: string; menuName: string }[]
    return { label, href, items: childrenmap, menuName }
  })
  const { ref, inView } = useInView(useInViewConfig)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return
      }
      setIsMenuOpen(open)
    }
  const handleOpenModal = () => {
    setModalOpen(true)
    // Focus the search input after modal opens
    setTimeout(() => {
      searchInputRef.current?.focus()
    }, 100)
  }
  const handleCloseModal = () => setModalOpen(false)
  return (
    <>
      <StyledHeader className="mui-fixed" data-egged-header>
        <InnerHeader $inView={inView}>
          <Mobile>
            {sheetOpen ? (
              <IconButton onClick={() => setSheetOpen(false)}>
                <Close />
              </IconButton>
            ) : (
              <StyledWrapperBurger>
                <IconButton
                  onClick={() => setIsMenuOpen((el) => !el)}
                  aria-expanded={isMenuOpen}
                >
                  {isMenuOpen ? (
                    <Close aria-label="סגור תפריט ראשי" />
                  ) : (
                    <Menu aria-label="תפריט ראשי" />
                  )}
                </IconButton>
              </StyledWrapperBurger>
            )}

            <SideDrawer
              isOpen={isMenuOpen}
              toggleDrawer={toggleDrawer}
              setIsDrawerOpen={setIsMenuOpen}
              menusmap={menusmap}
              locale={locale}
            />
          </Mobile>
          {/* LOGO */}
          <StyledLogoLink href="/">
            <Desktop>
              <Image
                src={getUmbracoMediaUrl(header.siteLogo) || ""}
                alt="Egged"
                width={240}
                height={48}
                priority
                style={{ height: 48, width: "auto" }}
              />
            </Desktop>
            <Mobile>
              <Image
                src={getUmbracoMediaUrl(header.siteLogo) || ""}
                alt="Egged"
                width={200}
                height={40}
                priority
                style={{ height: 40, width: "auto" }}
              />
            </Mobile>
          </StyledLogoLink>
          {/* MENU */}
          <Desktop height="100%">
            <MenuNav>
              {menusmap.map((el, x) => (
                <NavLink data={el} key={x} />
              ))}
            </MenuNav>
          </Desktop>
          {/* ACTIONS */}
          <ActionsDiv>
            <Desktop centerHorizontally>
              <StyledIconButton
                onClick={handleOpenModal}
                aria-expanded={modalOpen}
                aria-label={t("searchSiteAria")}
                aria-controls={modalOpen ? "search-modal" : undefined}
              >
                <Search />
              </StyledIconButton>
            </Desktop>
            {/* <Mobile centerHorizontally>
              <StyledIconButton
                onClick={handleOpenSheet}
                aria-expanded={sheetOpen}
              >
                <Search />
              </StyledIconButton>
            </Mobile> */}
            <Divider />
            <ContactMenu header={header} />
            <Divider />
            <LanguageMenu locale={locale} />
          </ActionsDiv>
        </InnerHeader>
        <SearchModal
          open={modalOpen}
          handleClose={handleCloseModal}
          culture={culture}
          searchInputRef={searchInputRef}
        />
      </StyledHeader>
    </>
  )
}
